import { unwrap, type MemberOut, type RoleOut, type TeamOut } from "@sagot/api-client";
import { useTranslation } from "@sagot/i18n";
import { consoleRoutes } from "@sagot/constants";
import { AppLink, Avatar, Badge, Button, DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, Icon, Select, Tooltip, cn, toast } from "@sagot/ui";
import { useMutation } from "@tanstack/react-query";
import { useErrorMessage } from "../lib/api-errors";
import { useInvalidateAccess } from "../roles/use-roles";
import { usePermissions } from "../session/permissions";
import { useApi } from "../session/session-provider";
import { useWorkspaceContext } from "../session/workspace-context";

export interface MemberRowActions {
  onEditRoles: (member: MemberOut) => void;
  onRemove: (member: MemberOut) => void;
}

interface MemberRowProps extends MemberRowActions {
  member: MemberOut;
  roles: readonly RoleOut[];
  teams: readonly TeamOut[];
  canManageRoles: boolean;
  canManageTeam: boolean;
  relative: (iso: string | null) => string;
}

/** Inline role select for the common case: exactly one workspace-wide built-in role. */
function RoleCell({ member, roles, canManage }: { member: MemberOut; roles: readonly RoleOut[]; canManage: boolean }) {
  const { t } = useTranslation();
  const api = useApi();
  const message = useErrorMessage();
  const invalidate = useInvalidateAccess();
  const only = member.assignments.length === 1 ? member.assignments[0] : undefined;
  const current = only?.scope_type === "all" ? roles.find((r) => r.id === only.role_id) : undefined;
  const change = useMutation({
    mutationFn: (roleId: string) =>
      unwrap(api.PUT("/v1/members/{membership_id}/roles", { params: { path: { membership_id: member.id } }, body: { assignments: [{ role_id: roleId, scope_type: "all", scope_ids: [] }] } })),
    onSuccess: async (_data, roleId) => {
      toast.success(t("team.roleChanged", { name: member.name, role: roles.find((r) => r.id === roleId)?.name ?? "" }));
      await invalidate();
    },
    onError: (caught) => toast.error(message(caught)),
  });
  if (!current) return <span className="text-xs font-semibold text-fg-muted">{t("team.multipleRoles", { count: member.assignments.length })}</span>;
  const locked = member.is_you || current.key === "owner" || !canManage;
  const reason = member.is_you ? t("team.ownRoleLocked") : current.key === "owner" ? t("team.ownerLocked") : undefined;
  const select = (
    <Select
      aria-label={t("team.roleSelect", { name: member.name })}
      className="h-8 w-44 text-xs"
      value={current.id}
      disabled={locked || change.isPending}
      title={reason}
      onChange={(event) => { change.mutate(event.target.value); }}
    >
      {roles.map((role) => (
        <option key={role.id} value={role.id}>
          {role.name}
        </option>
      ))}
    </Select>
  );
  return reason ? (
    <Tooltip content={reason}>
      <span className="inline-flex">{select}</span>
    </Tooltip>
  ) : (
    select
  );
}

function RowMenu({ member, canManageRoles, canManageTeam, onEditRoles, onRemove }: Omit<MemberRowProps, "roles" | "teams" | "relative">) {
  const { t } = useTranslation();
  const { summary } = useWorkspaceContext();
  const canAudit = usePermissions().can("audit.view");
  if (!canManageRoles && !canManageTeam && !canAudit && !member.is_you) return <span className="text-xs text-fg-subtle">—</span>;
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" size="sm" iconOnly aria-label={`${t("team.colActions")}: ${member.name}`}>
          <Icon name="more-horizontal" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-48">
        {canManageRoles && !member.is_you ? (
          <DropdownMenuItem onSelect={() => { onEditRoles(member); }}>
            <Icon name="shield-check" />
            {t("team.editRoles")}
          </DropdownMenuItem>
        ) : null}
        {canAudit ? (
          <DropdownMenuItem asChild>
            <AppLink href={`${consoleRoutes.settings(summary.slug, "audit")}?actor=${encodeURIComponent(member.user_id)}`}>
              <Icon name="scroll-text" />
              {t("team.viewActivity")}
            </AppLink>
          </DropdownMenuItem>
        ) : null}
        {member.is_you || canManageTeam ? (
          <DropdownMenuItem tone="danger" onSelect={() => { onRemove(member); }}>
            <Icon name={member.is_you ? "log-out" : "trash"} />
            {member.is_you ? t("team.leave") : t("team.remove")}
          </DropdownMenuItem>
        ) : null}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

export function MemberRow(props: MemberRowProps) {
  const { t } = useTranslation();
  const { member, teams, relative } = props;
  const memberTeams = teams.filter((team) => member.team_ids.includes(team.id));
  return (
    <tr>
      <td>
        <div className="flex items-center gap-2.5">
          <Avatar name={member.name} className={cn(member.is_you && "bg-brand-500 text-ink-950 dark:bg-brand-500 dark:text-ink-950")} />
          <div className="min-w-0">
            <div className="font-semibold">
              {member.name} {member.is_you ? <span className="text-xs font-medium text-fg-subtle">{t("team.you")}</span> : null}
            </div>
            <div className="truncate text-xs text-fg-subtle">{[member.email, member.job_title].filter(Boolean).join(" · ")}</div>
          </div>
        </div>
      </td>
      <td>
        <RoleCell member={member} roles={props.roles} canManage={props.canManageRoles} />
      </td>
      <td>
        {memberTeams.length ? (
          <span className="flex flex-wrap gap-1">
            {memberTeams.map((team) => (
              <Badge key={team.id}>{team.name}</Badge>
            ))}
          </span>
        ) : (
          <Badge>{t("team.noTeams")}</Badge>
        )}
      </td>
      <td>
        {member.two_factor_enabled ? (
          <Badge tone="success">
            <Icon name="shield-check" className="size-3" />
            {t("team.twoFaEnabled")}
          </Badge>
        ) : (
          <Badge tone="warning">
            <Icon name="alert-triangle" className="size-3" />
            {t("team.twoFaMissing")}
          </Badge>
        )}
      </td>
      <td className="whitespace-nowrap text-fg-muted">{member.is_you ? t("team.now") : relative(member.last_active_at)}</td>
      <td className="text-right">
        <RowMenu {...props} />
      </td>
    </tr>
  );
}
